import { useRef } from 'react';

import { formatMoney } from '@betterment/js-runtime';
import { Box, Button, Heading, Link, PancakeStack, Separator, Sheet, Text, Tooltip } from '@betterment/samba-react';

import { formatDrift, formatNumberWithDecimals } from '../../utils/NumbersHelper';

import type { SecurityGroupPosition, SecurityPosition, SecurityPositionBySecurity } from './data';

const detailRowCss = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: 'text.weak',
};

const SecurityPositionSection: React.FC<{
  position: SecurityPosition;
  sellButton?: React.ComponentType<{ securityId: string }>;
}> = ({ position, sellButton: SellButton }) => {
  const { security, amount } = position;
  const expenseRatio = security.expenseRatio?.value;

  return (
    <>
      <Separator />
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            {'name' in security && <Text>{security.name}</Text>}
            {SellButton && <SellButton securityId={security.id} />}
          </Box>
          {'url' in security ? (
            <Link href={security.url} target="_blank" rel="noopener noreferrer">
              {security.symbol}
            </Link>
          ) : (
            <Text>{security.symbol}</Text>
          )}
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
          {amount && (
            <Box css={detailRowCss}>
              <Text variant="text50">Balance</Text>
              <Text variant="text50">{formatMoney(amount)}</Text>
            </Box>
          )}
          <Box css={detailRowCss}>
            <Text variant="text50">Shares</Text>
            <Text variant="text50">{formatNumberWithDecimals(position.shares)} shares</Text>
          </Box>
          {expenseRatio && (
            <Box css={detailRowCss}>
              <Text variant="text50">Fund fee per year</Text>
              <Text variant="text50">{formatNumberWithDecimals(expenseRatio)}%</Text>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const SecurityPositionBySubAccountSection: React.FC<{
  position: SecurityPositionBySecurity;
  sellButton?: React.ComponentType<{ securityId: string }>;
}> = ({ position, sellButton: SellButton }) => {
  const securityPositionsBySubAccount = position.securityPositionsBySubAccount[0];
  const securityPositionsBySubAccountSecurityPosition = securityPositionsBySubAccount?.securityPositions[0];

  if (!securityPositionsBySubAccountSecurityPosition) {
    return null;
  }

  const { security } = securityPositionsBySubAccountSecurityPosition;
  const expenseRatio = security.expenseRatio?.value;

  return (
    <>
      <Separator />
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            {'name' in security && <Text>{security.name}</Text>}
            {SellButton && <SellButton securityId={security.id} />}
          </Box>
          {'url' in security ? (
            <Link href={security.url} target="_blank" rel="noopener noreferrer">
              {security.symbol}
            </Link>
          ) : (
            <Text>{security.symbol}</Text>
          )}
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
          <Box css={detailRowCss}>
            <Text variant="text50">Current weight</Text>
            <Text variant="text50">{formatNumberWithDecimals(position.currentWeight, 1)}%</Text>
          </Box>
          {expenseRatio && (
            <Box css={detailRowCss}>
              <Text variant="text50">Fund fee</Text>
              <Text variant="text50">{formatNumberWithDecimals(expenseRatio)}%/yr</Text>
            </Box>
          )}
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100', mt: 'space.100' }}>
          {position.securityPositionsBySubAccount.map(securityPosition => (
            <Box
              key={securityPosition.subAccount.id}
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'space.50',
                py: 'space.100',
              }}
            >
              <Text variant="text50">{securityPosition.subAccount.name}</Text>
              <Box css={detailRowCss}>
                <Text variant="text50" color="text.weak">
                  Balance
                </Text>
                <Text variant="text50" color="text.weak">
                  {formatMoney(securityPosition.amount)}
                </Text>
              </Box>
              <Box css={detailRowCss}>
                <Text variant="text50" color="text.weak">
                  Shares
                </Text>
                <Text variant="text50" color="text.weak">
                  {formatNumberWithDecimals(securityPosition.shares)} shares
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

type Props = {
  position: SecurityGroupPosition;
  sellButton?: React.ComponentType<{ securityId: string }>;
};

export const SecurityGroupPositionSheet: React.FC<React.PropsWithChildren<Props>> = ({ position, sellButton }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const hasMultipleSubAccounts = position.securityPositionsBySecurity.some(
    sp => sp.securityPositionsBySubAccount.length > 1
  );

  return (
    <Sheet.Content
      ref={contentRef}
      size="medium"
      variant="right"
      tabIndex={-1}
      onOpenAutoFocus={e => {
        e.preventDefault();
        contentRef.current?.focus();
      }}
    >
      <PancakeStack css={{ height: '100vh', display: 'flex', flexDirection: 'column', overflowY: 'scroll' }}>
        <PancakeStack.Header css={{ p: 'space.300', pt: 'space.500' }}>
          <Sheet.Title>{position.securityGroup.name}</Sheet.Title>
          <Sheet.Description>{position.securityGroup.description}</Sheet.Description>
        </PancakeStack.Header>
        <Separator css={{ fullWidthWithInset: 'space.300' }} />
        <PancakeStack.Main css={{ px: 'space.300', pb: 'space.300', flex: 1 }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100', py: 'space.200' }}>
            <Box css={detailRowCss}>
              <Text variant="caption">Current value</Text>
              <Text variant="caption">{formatMoney(position.amount)}</Text>
            </Box>
            <Box css={detailRowCss}>
              <Text variant="caption">Annual fund fees</Text>
              <Box>
                <Text variant="caption">{formatNumberWithDecimals(position.averageExpenseRatioPercent)}%</Text>
                &nbsp;&middot;&nbsp;
                <Text variant="caption">{formatMoney(position.fundFees)}</Text>
              </Box>
            </Box>
            <Box css={detailRowCss}>
              <Text variant="caption">Target weight</Text>
              <Text variant="caption">{formatNumberWithDecimals(position.securityGroup.targetWeight, 1)}%</Text>
            </Box>
            <Box css={detailRowCss}>
              <Text variant="caption">Current weight</Text>
              <Text variant="caption">{formatNumberWithDecimals(position.currentWeight, 1)}%</Text>
            </Box>
            <Box css={detailRowCss}>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.50' }}>
                <Text variant="caption">Drift</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true}>
                    <Tooltip.IconButton name="InfoCircle" />
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    Drift measures how much your assets move away from their target allocation due to your investments
                    growing at different rates.
                  </Tooltip.Content>
                </Tooltip>
              </Box>
              <Text variant="caption">{formatDrift(position.drift)}%</Text>
            </Box>
          </Box>
          <Separator />
          <Heading as="h2" variant="title200" css={{ pt: 'space.300', pb: 'space.200' }}>
            Assets
          </Heading>
          <Box css={{ display: 'grid', gap: 'space.200' }}>
            {hasMultipleSubAccounts
              ? position.securityPositionsBySecurity.map(sp => (
                  <SecurityPositionBySubAccountSection key={sp.id} position={sp} sellButton={sellButton} />
                ))
              : position.securityPositions?.map(sp => (
                  <SecurityPositionSection key={sp.security.id} position={sp} sellButton={sellButton} />
                ))}
          </Box>
        </PancakeStack.Main>
        <Separator css={{ fullWidthWithInset: 'space.300' }} />
        <PancakeStack.Footer css={{ p: 'space.300' }}>
          <Sheet.Close asChild={true}>
            <Button variant="solidSecondary">Close</Button>
          </Sheet.Close>
        </PancakeStack.Footer>
      </PancakeStack>
    </Sheet.Content>
  );
};
